import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsIcon from '@mui/icons-material/Directions';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TuneIcon from '@mui/icons-material/Tune';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CategoriaCardapio, ItemCarrinho, ProdutoCardapio, UnidadeDeliveryPublico } from '../../delivery.types';
import { useDeliveryStore } from '../../store/deliveryStore';
import { fmtMoeda } from '../../utils';
import DialogProduto from '../DialogProduto';

export default function StepMenu() {
  const { catalogo, carrinho, adicionarItem, removerItem, limparCarrinho, setStep, unidade } = useDeliveryStore();
  const [produtoAberto, setProdutoAberto] = useState<ProdutoCardapio | null>(null);
  const [editandoItem, setEditandoItem] = useState<{ item: ItemCarrinho; produto: ProdutoCardapio } | null>(null);
  const [confirmarReiniciar, setConfirmarReiniciar] = useState(false);
  const [categoriaAtiva, setCategoriaAtiva] = useState<number | null>(
    catalogo[0]?.codigoCategoria ?? null
  );
  const secaoRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const qtdCarrinho = carrinho.reduce((acc, i) => acc + i.quantidade, 0);
  const totalCarrinho = carrinho.reduce((acc, i) => {
    const extrasCompl = i.complementos
      .filter((c) => c.tipo === 'ADICIONAR')
      .reduce((s, c) => s + c.valorUnitario * c.quantidade, 0);
    const extrasGrupos = (i.grupoSelecoes ?? []).reduce((s, g) => {
      if (!g.itens.length) return s;
      return g.estrategiaPreco === 'MAIOR_VALOR'
        ? s + Math.max(...g.itens.map((gi) => gi.valorAdicional))
        : s + g.itens.reduce((gs, gi) => gs + gi.valorAdicional, 0);
    }, 0);
    return acc + (i.valorUnitario + extrasCompl + extrasGrupos) * i.quantidade;
  }, 0);

  function qtdNaCarrinho(codigoProduto: number) {
    return carrinho
      .filter((i) => i.codigoProduto === codigoProduto)
      .reduce((acc, i) => acc + i.quantidade, 0);
  }

  function adicionarDireto(produto: ProdutoCardapio, e: React.MouseEvent) {
    e.stopPropagation();
    adicionarItem({
      id: uuidv4(),
      codigoProduto: produto.codigoProduto,
      descricaoProduto: produto.descricao,
      quantidade: 1,
      valorUnitario: produto.valorVenda,
      observacao: '',
      complementos: [],
      grupoSelecoes: [],
    } as ItemCarrinho);
  }

  function irParaCategoria(cat: CategoriaCardapio) {
    setCategoriaAtiva(cat.codigoCategoria);
    secaoRefs.current[cat.codigoCategoria]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const temComposicao = (p: ProdutoCardapio) =>
    (p.composicao && p.composicao.length > 0) || (p.grupos && p.grupos.length > 0);

  function abrirEdicaoItem(item: ItemCarrinho) {
    for (const cat of catalogo) {
      const p = cat.produtos.find(p => p.codigoProduto === item.codigoProduto);
      if (p) { setEditandoItem({ item, produto: p }); return; }
    }
  }

  const ehPersonalizavel = (item: ItemCarrinho) =>
    item.complementos.length > 0 || item.grupoSelecoes.length > 0;

  if (catalogo.length === 0) {
    return (
      <Box textAlign="center" py={8} flex={1}>
        <Typography variant="h6" color="text.secondary">Nenhum item disponível no momento.</Typography>
        <Typography variant="body2" color="text.disabled" mt={1}>Tente novamente mais tarde.</Typography>
      </Box>
    );
  }

  // ── Desktop: sidebar categorias | grid produtos | sidebar carrinho ──────────
  if (isDesktop) {
    return (
      <>
        {/* Sidebar esquerda — categorias */}
        <Box sx={{ width: 220, flexShrink: 0, position: 'sticky', top: 80, alignSelf: 'flex-start' }}>
          {/* Info da unidade compacta */}
          {unidade && (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
              {unidade.empresaLogo ? (
                <Box component="img"
                  src={`data:image/png;base64,${unidade.empresaLogo}`}
                  alt={unidade.empresaNome ?? unidade.nome}
                  sx={{ width: '100%', height: 80, objectFit: 'cover' }}
                />
              ) : (
                <Box sx={{ bgcolor: 'primary.main', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StorefrontIcon sx={{ fontSize: 32, color: 'primary.contrastText', opacity: 0.7 }} />
                </Box>
              )}
              <Box px={1.5} py={1}>
                <Typography fontWeight={700} fontSize="0.85rem" noWrap>{unidade.empresaNome ?? unidade.nome}</Typography>
                {enderecoFormatado(unidade) && (
                  <Box
                    component="a"
                    href={linkNavegacao(unidade)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Abrir endereço no app de navegação"
                    sx={{
                      display: 'inline-flex', alignItems: 'flex-start', gap: 0.25,
                      mt: 0.25, color: 'primary.main', textDecoration: 'none',
                      fontSize: '0.72rem', lineHeight: 1.3,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 12, mt: '1px', flexShrink: 0 }} />
                    <span>{enderecoFormatado(unidade)}</span>
                    <DirectionsIcon sx={{ fontSize: 12, mt: '1px', flexShrink: 0 }} />
                  </Box>
                )}
                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {(unidade.taxaEntrega ?? 0) === 0
                    ? <Chip icon={<TwoWheelerIcon />} label="Grátis" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                    : <Chip icon={<TwoWheelerIcon />} label={fmtMoeda(unidade.taxaEntrega)} size="small" sx={{ fontSize: '0.7rem' }} />}
                  {(unidade.tempoEntregaMinutos ?? 0) > 0 && (
                    <Chip icon={<QueryBuilderIcon />} label={`~${unidade.tempoEntregaMinutos}min`} size="small" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Typography variant="overline" color="text.secondary" fontWeight={700} display="block" mb={1}>
            Categorias
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {catalogo.map((cat) => (
              <ButtonBase
                key={cat.codigoCategoria}
                onClick={() => irParaCategoria(cat)}
                sx={{
                  width: '100%', textAlign: 'left',
                  px: 1.5, py: 1, borderRadius: 2,
                  bgcolor: categoriaAtiva === cat.codigoCategoria ? 'primary.main' : 'transparent',
                  color: categoriaAtiva === cat.codigoCategoria ? 'primary.contrastText' : 'text.primary',
                  fontWeight: categoriaAtiva === cat.codigoCategoria ? 700 : 400,
                  fontSize: '0.9rem',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: categoriaAtiva === cat.codigoCategoria ? 'primary.dark' : 'action.hover' },
                }}
              >
                {cat.descricaoCategoria}
              </ButtonBase>
            ))}
          </Box>
        </Box>

        {/* Centro — produtos em grid 2 colunas */}
        <Box flex={1} minWidth={0}>
          {catalogo.map((cat) => (
            <Box
              key={cat.codigoCategoria}
              mb={5}
              ref={(el) => { secaoRefs.current[cat.codigoCategoria] = el as HTMLDivElement | null; }}
            >
              <CategoriaHeader descricao={cat.descricaoCategoria} qtdProdutos={cat.produtos.length} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                {cat.produtos.map((produto) => (
                  <ProdutoCard
                    key={produto.codigoProduto}
                    produto={produto}
                    categoriaDescricao={cat.descricaoCategoria}
                    qtd={qtdNaCarrinho(produto.codigoProduto)}
                    simples={!temComposicao(produto)}
                    onDetalhes={() => setProdutoAberto(produto)}
                    onAdicionar={(e) => adicionarDireto(produto, e)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Sidebar direita — resumo do carrinho */}
        <Box sx={{ width: 300, flexShrink: 0, position: 'sticky', top: 80, alignSelf: 'flex-start' }}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box sx={{ bgcolor: 'primary.main', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon sx={{ color: 'primary.contrastText', fontSize: 20 }} />
              <Typography fontWeight={700} color="primary.contrastText" fontSize="0.95rem">
                Meu pedido
              </Typography>
            </Box>

            {qtdCarrinho === 0 ? (
              <Box px={2} py={3} textAlign="center">
                <ShoppingCartIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Seu carrinho está vazio
                </Typography>
                <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                  Escolha os itens que deseja e adicione aqui 👆
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box px={1.5} py={1.5} display="flex" flexDirection="column" gap={0.5} maxHeight={320} sx={{ overflowY: 'auto' }}>
                  {carrinho.map((item) => {
                    const extrasCompl = item.complementos
                      .filter((c) => c.tipo === 'ADICIONAR')
                      .reduce((s, c) => s + c.valorUnitario * c.quantidade, 0);
                    const extrasGrupos = (item.grupoSelecoes ?? []).reduce((s, g) => {
                      if (!g.itens.length) return s;
                      return g.estrategiaPreco === 'MAIOR_VALOR'
                        ? s + Math.max(...g.itens.map((gi) => gi.valorAdicional))
                        : s + g.itens.reduce((gs, gi) => gs + gi.valorAdicional, 0);
                    }, 0);
                    const precoUnit = item.valorUnitario + extrasCompl + extrasGrupos;
                    const personalizavel = ehPersonalizavel(item);

                    return (
                      <Box key={item.id} display="flex" alignItems="center" gap={0.5}
                        sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' }, px: 0.5 }}>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" noWrap title={item.descricaoProduto}>
                            {item.quantidade}× {item.descricaoProduto}
                          </Typography>
                          {(item.grupoSelecoes ?? []).map((g) => (
                            <Typography key={g.codigoGrupo} variant="caption" color="text.secondary" display="block" noWrap>
                              {g.itens.map(i => i.descricao).join(', ')}
                            </Typography>
                          ))}
                        </Box>
                        <Typography variant="body2" fontWeight={600} flexShrink={0} fontSize="0.8rem">
                          {fmtMoeda(precoUnit * item.quantidade)}
                        </Typography>
                        {personalizavel && (
                          <IconButton size="small" onClick={() => abrirEdicaoItem(item)} sx={{ color: 'primary.main', p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => removerItem(item.id)} sx={{ color: 'error.main', p: 0.5 }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
                <Divider />
                <Box px={2} py={1.25} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700} fontSize="0.9rem">Total</Typography>
                  <Typography fontWeight={800} color="primary">{fmtMoeda(totalCarrinho)}</Typography>
                </Box>
                <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="contained" fullWidth size="large"
                    onClick={() => setStep(2)} disableElevation
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Fazer pedido →
                  </Button>
                  <Button
                    variant="text" fullWidth size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setConfirmarReiniciar(true)}
                    sx={{ color: 'text.secondary', fontSize: '0.78rem' }}
                  >
                    Reiniciar pedido
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {produtoAberto && (
          <DialogProduto produto={produtoAberto} onClose={() => setProdutoAberto(null)} />
        )}

        {editandoItem && (
          <DialogProduto
            produto={editandoItem.produto}
            itemEditando={editandoItem.item}
            onClose={() => setEditandoItem(null)}
          />
        )}

        <Dialog open={confirmarReiniciar} onClose={() => setConfirmarReiniciar(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Reiniciar pedido?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Todos os itens do carrinho serão removidos. Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmarReiniciar(false)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={() => { limparCarrinho(); setConfirmarReiniciar(false); }}>
              Reiniciar
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // ── Mobile: coluna única + barra de categorias + barra do carrinho ──────────
  return (
    <Box sx={{ pb: qtdCarrinho > 0 ? 10 : 3, width: '100%' }}>
      {/* Banner da unidade */}
      {unidade && <BannerUnidade unidade={unidade} />}

      {/* Filtro por categoria */}
      {catalogo.length > 1 && (
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 20,
          bgcolor: 'background.default',
          pt: 1, pb: 1.5, mx: -2, px: 2,
          display: 'flex', gap: 1, overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          borderBottom: '1px solid', borderColor: 'divider',
          mb: 2,
        }}>
          {catalogo.map((cat) => (
            <Chip
              key={cat.codigoCategoria}
              label={cat.descricaoCategoria}
              onClick={() => irParaCategoria(cat)}
              color={categoriaAtiva === cat.codigoCategoria ? 'primary' : 'default'}
              variant={categoriaAtiva === cat.codigoCategoria ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0, fontWeight: 600, fontSize: '0.85rem' }}
            />
          ))}
        </Box>
      )}

      {catalogo.map((cat) => (
        <Box
          key={cat.codigoCategoria}
          mb={4}
          ref={(el) => { secaoRefs.current[cat.codigoCategoria] = el as HTMLDivElement | null; }}
        >
          <CategoriaHeader descricao={cat.descricaoCategoria} qtdProdutos={cat.produtos.length} />
          <Box display="flex" flexDirection="column" gap={1.5}>
            {cat.produtos.map((produto) => (
              <ProdutoCard
                key={produto.codigoProduto}
                produto={produto}
                categoriaDescricao={cat.descricaoCategoria}
                qtd={qtdNaCarrinho(produto.codigoProduto)}
                simples={!temComposicao(produto)}
                onDetalhes={() => setProdutoAberto(produto)}
                onAdicionar={(e) => adicionarDireto(produto, e)}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* Barra do carrinho fixada no bottom */}
      {qtdCarrinho > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          p: 2, bgcolor: 'background.default',
          borderTop: '1px solid', borderColor: 'divider', zIndex: 100,
        }}>
          <Button
            variant="contained" fullWidth size="large"
            onClick={() => setStep(2)} disableElevation
            startIcon={<ShoppingCartIcon />}
            sx={{
              borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1rem',
              display: 'flex', justifyContent: 'space-between', px: 2,
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{
                bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 1,
                px: 0.75, fontSize: '0.85rem', fontWeight: 800,
              }}>
                {qtdCarrinho}
              </Box>
              Ver carrinho e finalizar
            </Box>
            <Box component="span" fontWeight={800}>{fmtMoeda(totalCarrinho)}</Box>
          </Button>
        </Box>
      )}

      {produtoAberto && (
        <DialogProduto produto={produtoAberto} onClose={() => setProdutoAberto(null)} />
      )}
    </Box>
  );
}

// ── Helper de navegação ───────────────────────────────────────────────────────

function linkNavegacao(unidade: UnidadeDeliveryPublico): string {
  const partes = [
    unidade.empresaEndereco,
    unidade.empresaCidade,
    unidade.empresaUf,
  ].filter(Boolean).join(', ');
  return `https://maps.google.com/?q=${encodeURIComponent(partes)}`;
}

function enderecoFormatado(unidade: UnidadeDeliveryPublico): string | null {
  const partes = [
    unidade.empresaEndereco,
    unidade.empresaCidade && unidade.empresaUf
      ? `${unidade.empresaCidade}/${unidade.empresaUf}`
      : unidade.empresaCidade ?? unidade.empresaUf,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(' — ') : null;
}

// ── Banner da unidade (mobile) ────────────────────────────────────────────────

function BannerUnidade({ unidade }: { unidade: UnidadeDeliveryPublico | null }) {
  if (!unidade) return null;
  const endereco = enderecoFormatado(unidade);
  return (
    <Box sx={{ mb: 2, mx: -2 }}>
      {unidade.empresaLogo ? (
        <Box component="img"
          src={`data:image/png;base64,${unidade.empresaLogo}`}
          alt={unidade.empresaNome ?? unidade.nome}
          sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box sx={{ bgcolor: 'primary.main', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StorefrontIcon sx={{ fontSize: 48, color: 'primary.contrastText', opacity: 0.5 }} />
        </Box>
      )}
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography fontWeight={800} fontSize="1rem">{unidade.empresaNome ?? unidade.nome}</Typography>
        {unidade.mensagemBemVindo && (
          <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" mt={0.25}>
            {unidade.mensagemBemVindo}
          </Typography>
        )}
        {endereco && (
          <Box
            component="a"
            href={linkNavegacao(unidade)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Abrir endereço no app de navegação"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              mt: 0.75, color: 'primary.main', textDecoration: 'none',
              fontSize: '0.8rem', fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <LocationOnIcon sx={{ fontSize: 15 }} />
            {endereco}
            <DirectionsIcon sx={{ fontSize: 15, ml: 0.25 }} />
          </Box>
        )}
        <Box display="flex" gap={0.75} mt={0.75} flexWrap="wrap">
          {(unidade.taxaEntrega ?? 0) === 0
            ? <Chip icon={<TwoWheelerIcon />} label="Entrega grátis" size="small" color="success" />
            : <Chip icon={<TwoWheelerIcon />} label={`Entrega: ${fmtMoeda(unidade.taxaEntrega)}`} size="small" />}
          {(unidade.tempoEntregaMinutos ?? 0) > 0 && (
            <Chip icon={<QueryBuilderIcon />} label={`~${unidade.tempoEntregaMinutos} min`} size="small" />
          )}
          {unidade.permiteRetirada && <Chip label="Retirada no local" size="small" variant="outlined" />}
          {(unidade.pedidoMinimo ?? 0) > 0 && (
            <Chip label={`Mínimo: ${fmtMoeda(unidade.pedidoMinimo!)}`} size="small" variant="outlined" />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Cabeçalho de categoria ────────────────────────────────────────────────────

function CategoriaHeader({ descricao, qtdProdutos }: { descricao: string; qtdProdutos: number }) {
  return (
    <Box
      display="flex" alignItems="center" gap={1} mb={1.5}
      sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 1.5, py: 0.5 }}
    >
      <LocalOfferIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      <Typography variant="h6" fontWeight={800} fontSize="1.05rem">
        {descricao}
      </Typography>
      <Typography variant="caption" color="text.disabled" ml={0.5}>
        ({qtdProdutos} {qtdProdutos === 1 ? 'item' : 'itens'})
      </Typography>
    </Box>
  );
}

// ── Card de produto ───────────────────────────────────────────────────────────

function ProdutoCard({
  produto, categoriaDescricao, qtd, simples, onDetalhes, onAdicionar,
}: {
  produto: ProdutoCardapio;
  categoriaDescricao: string;
  qtd: number;
  simples: boolean;
  onDetalhes: () => void;
  onAdicionar: (e: React.MouseEvent) => void;
}) {
  return (
    <Box sx={{
      borderRadius: 3, border: '2px solid',
      borderColor: qtd > 0 ? 'primary.main' : 'divider',
      bgcolor: 'background.paper',
      overflow: 'hidden',
      transition: 'border-color 0.15s, background 0.15s',
      display: 'flex', flexDirection: 'column',
      boxShadow: qtd > 0 ? 2 : 0,
    }}>
      <ButtonBase
        component="div"
        onClick={onDetalhes}
        aria-label={`Ver detalhes de ${produto.descricao}`}
        sx={{ display: 'flex', width: '100%', textAlign: 'left', minHeight: 110, flex: 1 }}
      >
        {produto.imagemBase64 ? (
          <Box
            component="img"
            src={produto.imagemBase64.startsWith('data:') ? produto.imagemBase64 : `data:image/jpeg;base64,${produto.imagemBase64}`}
            alt={produto.descricao}
            sx={{ width: 110, height: 110, objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <Box sx={{
            width: 110, height: 110, flexShrink: 0,
            bgcolor: 'action.hover',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShoppingCartIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
          </Box>
        )}

        <Box sx={{ flex: 1, px: 2, py: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            {/* Badge de categoria */}
            <Chip
              label={categoriaDescricao}
              size="small"
              sx={{ mb: 0.75, fontSize: '0.7rem', height: 20, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700, borderRadius: 1 }}
            />
            <Typography fontWeight={700} fontSize="1rem" lineHeight={1.3}>
              {produto.descricao}
            </Typography>
            {produto.descricaoDetalhada && (
              <Typography color="text.secondary" mt={0.5} fontSize="0.85rem" lineHeight={1.4}
                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {produto.descricaoDetalhada}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
            <Typography fontWeight={800} fontSize="1.1rem" color="primary">
              {fmtMoeda(produto.valorVenda)}
            </Typography>
            {qtd > 0 && (
              <Chip label={`${qtd} no carrinho`} size="small" color="primary" sx={{ fontWeight: 700, fontSize: '0.75rem' }} />
            )}
          </Box>
        </Box>
      </ButtonBase>

      <Divider />
      <Box sx={{ px: 1.5, py: 1.25 }}>
        {simples ? (
          <Button variant="contained" size="medium" fullWidth startIcon={<AddIcon />}
            onClick={onAdicionar} aria-label={`Adicionar ${produto.descricao} ao carrinho`}
            disableElevation sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.9rem', py: 1 }}>
            Adicionar — {fmtMoeda(produto.valorVenda)}
          </Button>
        ) : (
          <Button variant="outlined" size="medium" fullWidth startIcon={<TuneIcon />}
            onClick={onDetalhes} aria-label={`Escolher opções de ${produto.descricao}`}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.9rem', py: 1 }}>
            Personalizar — {fmtMoeda(produto.valorVenda)}
          </Button>
        )}
      </Box>
    </Box>
  );
}
